import { AxiosRequestConfig } from 'axios';

import { getApiUrl, getServiceBaseUrl } from '@/config/appConfig';

import createAxiosInstance from './axios';

const scrmBaseUrl = getServiceBaseUrl('scrm');
const guideBaseUrl = getServiceBaseUrl('guide');

/** 默认 SCRM 请求实例：不触发全局遮罩，页面内 loading 由组件自行管理。 */
export const request = createAxiosInstance(scrmBaseUrl);
/** 兼容旧命名：语义等同于 request，后续新代码优先使用 request。 */
export const requestNotLoading = request;
/** 显式全局遮罩实例：仅用于导入、导出、保存等必须阻断交互的操作。 */
export const requestWithGlobalLoading = createAxiosInstance(scrmBaseUrl, true);

/** 默认导购请求实例：不触发全局遮罩，页面内 loading 由组件自行管理。 */
export const guideRequest = createAxiosInstance(guideBaseUrl);
/** 兼容旧命名：语义等同于 guideRequest，后续新代码优先使用 guideRequest。 */
export const guideRequestNotLoading = guideRequest;
/** 显式导购全局遮罩实例：仅用于必须阻断交互的导购操作。 */
export const guideRequestWithGlobalLoading = createAxiosInstance(
  guideBaseUrl,
  true
);

export const uploadUrl = getApiUrl('scrm', '/util/upload');

type FetchOptions = AxiosRequestConfig & {
  /**
   * 是否使用全局遮罩 loading。
   * 默认 false，避免和组件内的 Spin、Skeleton、按钮 loading 叠加。
   */
  useGlobalLoading?: boolean;
  /** 是否自动解包响应的 data 字段，默认 true */
  returnData?: boolean;
  /** 是否禁止弹出错误提示，默认跟随 returnData */
  noMessage?: boolean;
};

/**
 * 通用请求函数，根据 method 自动将 data 放入 body（POST）或 params（GET）。
 * useGlobalLoading 控制是否显示全局遮罩，returnData 控制是否自动解包响应 data 字段。
 */
// eslint-disable-next-line max-params
export default function fetch<R>(
  url: string,
  method: string,
  data: Record<string, unknown> | {} = {},
  options?: FetchOptions
) {
  const {
    useGlobalLoading = false,
    returnData = true,
    noMessage,
    ...rest
  } = options || {};
  const config: FetchOptions = {
    url,
    method,
    returnData,
    noMessage: noMessage ?? returnData,
  };
  if (method === 'POST') {
    config.data = data;
  } else if (method === 'GET') {
    config.params = data;
  }

  const requestFun = useGlobalLoading ? requestWithGlobalLoading : request;
  return requestFun({
    ...config,
    ...rest,
  }) as unknown as Promise<R>;
}

/**
 * 工厂函数：为固定 URL + method 的接口生成类型安全的请求函数。
 * 泛型参数 T 控制是否自动解包 data 字段（T=true 返回 RES['data']，T=false 返回完整 RES）。
 */
export function createFetch<REQ, RES extends { data: Record<string, unknown> }>(
  url: string,
  method: string
) {
  return <T extends boolean = false>(
    data: REQ,
    options?: FetchOptions & {
      returnData?: T;
    }
  ): Promise<T extends true ? RES['data'] : RES> => {
    const {
      useGlobalLoading = false,
      returnData = true,
      ...rest
    } = options || {};
    const config: FetchOptions = {
      url,
      method,
      returnData,
    };
    if (method === 'POST') {
      config.data = data;
    } else if (method === 'GET') {
      config.params = data;
    }
    const requestFun = useGlobalLoading ? requestWithGlobalLoading : request;
    return requestFun({
      ...config,
      ...rest,
    }) as unknown as Promise<T extends true ? RES['data'] : RES>;
  };
}
